import { getApiUrl } from '../lib/config';

interface User {
  id: number;
  username: string;
  email: string;
  displayName: string;
  role: string;
}

interface Video {
  id: number;
  title: string;
  description: string;
  file_path: string;
  thumbnail_url?: string;
  duration_seconds?: number;
  upload_date: string;
  view_count: number;
  show_title?: string;
  uploader_name?: string;
}

interface Show {
  id: number;
  title: string;
  description: string;
  host_name: string;
  category?: string;
  duration_minutes: number;
  cover_image_url?: string;
}

interface Schedule {
  id: number;
  show_title: string;
  host_name: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  status: string;
}

interface LiveStream {
  id: number;
  title: string;
  description?: string;
  stream_url: string;
  host_name: string;
  show_title?: string;
  viewer_count: number;
  status: string;
  start_time: string;
}

class ApiService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  private async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = getApiUrl(`api${endpoint}`);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Authentication
  async login(username: string, password: string): Promise<{ user: User; token: string }> {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    this.token = response.token;
    localStorage.setItem('auth_token', response.token);
    return response;
  }

  async register(userData: {
    username: string;
    email: string;
    password: string;
    displayName: string;
    role?: string;
  }): Promise<{ user: User; token: string }> {
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    this.token = response.token;
    localStorage.setItem('auth_token', response.token);
    return response;
  }

  async getProfile(): Promise<{ user: User }> {
    return this.request('/auth/profile');
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  // Videos
  async getVideos(params: {
    page?: number;
    limit?: number;
    show_id?: number;
    search?: string;
  } = {}): Promise<{ videos: Video[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });
    
    return this.request(`/videos?${queryParams}`);
  }

  async getVideo(id: number): Promise<{ video: Video }> {
    return this.request(`/videos/${id}`);
  }

  async uploadVideo(formData: FormData): Promise<{ video: Video }> {
    const url = getApiUrl('api/videos');
    const headers: Record<string, string> = {};

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async updateVideo(id: number, data: Partial<Video>): Promise<{ video: Video }> {
    return this.request(`/videos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVideo(id: number): Promise<{ message: string }> {
    return this.request(`/videos/${id}`, {
      method: 'DELETE',
    });
  }

  // Schedule
  async getSchedule(params: {
    start_date?: string;
    end_date?: string;
    show_id?: number;
  } = {}): Promise<{ schedule: Schedule[] }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });
    
    return this.request(`/schedule?${queryParams}`);
  }

  async getTodaySchedule(): Promise<{ schedule: Schedule[] }> {
    return this.request('/schedule/today');
  }

  async getCurrentSchedule(): Promise<{ current: Schedule | null; next: Schedule | null }> {
    return this.request('/schedule/current');
  }

  async createSchedule(data: {
    show_id: number;
    scheduled_date: string;
    start_time: string;
    end_time: string;
    host_id: number;
    is_recurring?: boolean;
    recurrence_type?: string;
    notes?: string;
  }): Promise<{ schedule: Schedule }> {
    return this.request('/schedule', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSchedule(id: number, data: Partial<Schedule>): Promise<{ schedule: Schedule }> {
    return this.request(`/schedule/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSchedule(id: number): Promise<{ message: string }> {
    return this.request(`/schedule/${id}`, {
      method: 'DELETE',
    });
  }

  // Streams
  async getStreams(params: { status?: string; limit?: number } = {}): Promise<{ streams: LiveStream[] }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });
    
    return this.request(`/streams?${queryParams}`);
  }

  async getCurrentStream(): Promise<{ stream: LiveStream | null }> {
    return this.request('/streams/current');
  }

  async startStream(data: {
    title: string;
    description?: string;
    show_id?: number;
    stream_source?: string;
    quality_settings?: any;
  }): Promise<{ stream: LiveStream }> {
    return this.request('/streams/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async stopStream(id: number): Promise<{ message: string }> {
    return this.request(`/streams/stop/${id}`, {
      method: 'POST',
    });
  }

  async updateStream(id: number, data: Partial<LiveStream>): Promise<{ stream: LiveStream }> {
    return this.request(`/streams/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async joinStream(id: number, userData?: any): Promise<{ message: string }> {
    return this.request(`/streams/${id}/join`, {
      method: 'POST',
      body: JSON.stringify(userData || {}),
    });
  }

  // Shows
  async getShows(): Promise<{ shows: Show[] }> {
    return this.request('/users/shows');
  }

  async createShow(data: {
    title: string;
    description: string;
    category?: string;
    duration_minutes?: number;
    cover_image_url?: string;
    host_id?: number;
  }): Promise<{ show: Show }> {
    return this.request('/users/shows', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateShow(id: number, data: Partial<Show>): Promise<{ show: Show }> {
    return this.request(`/users/shows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteShow(id: number): Promise<{ message: string }> {
    return this.request(`/users/shows/${id}`, {
      method: 'DELETE',
    });
  }

  // Statistics
  async getStats(): Promise<{
    totalVideos: number;
    totalUsers: number;
    totalShows: number;
    liveStreams: number;
    totalViews: number;
  }> {
    return this.request('/stats');
  }

  async getUserStats(): Promise<{
    shows: number;
    videos: number;
    scheduledShows: number;
    totalViews: number;
  }> {
    return this.request('/users/stats');
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health');
  }

  // Document collaboration
  async getDocument(): Promise<{
    id: number;
    content: string;
    version: number;
    lastUpdated: string;
  }> {
    return this.request('/document');
  }

  async updateDocument(data: {
    content: string;
    operation?: string;
    contentBefore?: string;
    positionStart?: number;
    positionEnd?: number;
    sessionId?: string;
    fingerprint?: string;
  }): Promise<{
    id: number;
    content: string;
    version: number;
    lastUpdated: string;
  }> {
    return this.request('/document', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getDocumentLogs(params: {
    page?: number;
    limit?: number;
    ip?: string;
    session?: string;
  } = {}): Promise<{
    logs: any[];
    pagination: any;
  }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });
    
    return this.request(`/document/logs?${queryParams}`);
  }
}

export const apiService = new ApiService();
export type { User, Video, Show, Schedule, LiveStream }; 